import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';
import { Icon } from '../../tcn/ui-kit';
import { TCN } from '../../tcn/ui-kit/tokens';
import useUnreadAlerts from './useUnreadAlerts';

/**
 * Module 7 — Bell icon with unread badge.
 *
 * Drop into any TopBar trailing slot. Uses the shared useUnreadAlerts hook
 * for the count + realtime subscription so multiple bells share a single
 * RLS-scoped channel per page.
 *
 * Tap → navigate to /tcn/me/alerts.
 */
const AlertBell = ({ size = 36 }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { count } = useUnreadAlerts(user?.id);
  const hasUnread = count > 0;

  return (
    <button
      type="button"
      onClick={() => navigate('/tcn/me/alerts')}
      aria-label={t('tcn.m7.bell.aria', 'Alerts ({{count}})', { count })}
      style={{
        position: 'relative',
        background: 'transparent',
        border: 'none',
        color: hasUnread ? TCN.cyan : TCN.textDim,
        cursor: 'pointer',
        padding: 4,
        width: size,
        height: size,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Icon name="bell" size={20} />
      {hasUnread && (
        // Discreet unread dot (mockup A1.1) — exact count is exposed via aria-label.
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 6,
            insetInlineEnd: 6,
            width: 7,
            height: 7,
            borderRadius: 99,
            background: TCN.magenta,
            border: `1.5px solid ${TCN.bg}`,
          }}
        />
      )}
    </button>
  );
};

export default AlertBell;
